import React from "react";
import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import { ROUTS } from "../../../../routs/routs";
import { edmIsEnabled } from "../../../../utils/edmIsEnabled";
import {
  CogIcon,
  HomeIcon,
  TruckIcon,
  ArchiveIcon,
  SupportIcon,
  ChartBarIcon,
  ViewGridIcon,
  ChartPieIcon,
  CalculatorIcon,
  ShoppingCartIcon,
  IdentificationIcon,
} from "@heroicons/react/outline";

export type typeSidebarData = {
  id: string;
  name: React.ReactElement<TransProps>;
  icon: (props: React.ComponentProps<"svg">) => JSX.Element;
  href: string;
  current: boolean;
  children?: typeSidebarDataChildren[];
}[];

export type typeSidebarDataChildren = {
  id: string;
  href: string;
  current: boolean;
  name: React.ReactElement<TransProps>;
};

export const navigationMenu: typeSidebarData = [
  {
    id: "commodity",
    name: <Trans>Commodity</Trans>,
    icon: ViewGridIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "products",
        name: <Trans>Products</Trans>,
        href: ROUTS.COMMODITY_PRODUCTS_DASHBOARD,
        current: false,
      },
      {
        id: "catalog",
        name: <Trans>Categories</Trans>,
        href: ROUTS.COMMODITY_CATEGORIES_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "sumUp",
    name: <Trans>Sum up</Trans>,
    icon: ChartBarIcon,
    href: "#",
    current: false,
    children:
      process.env.REACT_APP_ENCASHMENT === "true"
        ? [
            {
              id: "sales",
              name: <Trans>Sales</Trans>,
              href: ROUTS.SUMUP_SALES_DASHBOARD,
              current: false,
            },
            {
              id: "refunds",
              name: <Trans>Refunds</Trans>,
              href: ROUTS.SUMUP_REFUNDS_DASHBOARD,
              current: false,
            },
            {
              id: "encashment",
              name: <Trans>Encashment</Trans>,
              href: ROUTS.SUMUP_ENCASHMENT_DASHBOARD,
              current: false,
            },
          ]
        : [
            {
              id: "sales",
              name: <Trans>Sales</Trans>,
              href: ROUTS.SUMUP_SALES_DASHBOARD,
              current: false,
            },
            {
              id: "refunds",
              name: <Trans>Refunds</Trans>,
              href: ROUTS.SUMUP_REFUNDS_DASHBOARD,
              current: false,
            },
          ],
  },
  {
    id: "warehouse",
    name: <Trans>Warehouse</Trans>,
    icon: HomeIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "warehouseProducts",
        name: <Trans>Warehouse products</Trans>,
        href: ROUTS.WAREHOUSE_PRODUCTS_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "till",
    name: <Trans>Tills</Trans>,
    icon: CalculatorIcon,
    href: "#",
    current: false,
    children:
      process.env.REACT_APP_CUSTOMER !== "SKAPPSK"
        ? [
            {
              id: "tills",
              name: <Trans>Tills list</Trans>,
              href: ROUTS.STORE_CASHIERDESKMANAGEMENT_DASHBOARD,
              current: false,
            },
            {
              id: "closingZReport",
              name: <Trans>Closing Z report</Trans>,
              href: ROUTS.SUMUP_CLOSINGZREPORT_DASHBOARD,
              current: false,
            },
            {
              id: "handoverRecord",
              name: <Trans>Handover record</Trans>,
              href: ROUTS.SUMUP_HANDOVERRECORD_DASHBOARD,
              current: false,
            },
            {
              id: "posConfiguration",
              name: <Trans>Pos configurations</Trans>,
              href: ROUTS.SETTINGS_POS_CONFIGURATION_DASHBOARD,
              current: false,
            },
          ]
        : [
            {
              id: "tills",
              name: <Trans>Tills list</Trans>,
              href: ROUTS.STORE_CASHIERDESKMANAGEMENT_DASHBOARD,
              current: false,
            },
            {
              id: "handoverRecord",
              name: <Trans>Handover record</Trans>,
              href: ROUTS.SUMUP_HANDOVERRECORD_DASHBOARD,
              current: false,
            },
            {
              id: "posConfiguration",
              name: <Trans>Pos configurations</Trans>,
              href: ROUTS.SETTINGS_POS_CONFIGURATION_DASHBOARD,
              current: false,
            },
          ],
  },
  {
    id: "store",
    name: <Trans>Stores</Trans>,
    icon: ShoppingCartIcon,
    href: ROUTS.STORE_STORES_DASHBOARD,
    current: false,
  },
  {
    id: "statistics",
    name: <Trans>Statistics</Trans>,
    icon: ChartPieIcon,
    href: ROUTS.STATISTICS_DASHBOARD,
    current: false,
  },
  {
    id: "orders",
    name: <Trans>Orders</Trans>,
    icon: TruckIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "ordersList",
        name: <Trans>Orders list</Trans>,
        href: ROUTS.ORDERS_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "staff",
    name: <Trans>Staff</Trans>,
    icon: IdentificationIcon,
    href: ROUTS.STAFF_DASHBOARD,
    current: false,
  },
  {
    id: "support",
    name: <Trans>Support</Trans>,
    icon: SupportIcon,
    href: ROUTS.SUPPORT_DASHBOARD,
    current: false,
  },
  {
    id: "settings",
    name: <Trans>Settings</Trans>,
    icon: CogIcon,
    href: ROUTS.SETTINGS_DASHBOARD,
    current: false,
  },
  {
    href: "#",
    id: "archive",
    current: false,
    icon: ArchiveIcon,
    name: <Trans>Archive</Trans>,
    children: [
      {
        current: false,
        id: "archiveSalesList",
        name: <Trans>Sales</Trans>,
        href: ROUTS.ARCHIVE_SALES_DASHBOARD,
      },
      {
        current: false,
        id: "archiveRefundsList",
        name: <Trans>Refunds</Trans>,
        href: ROUTS.ARCHIVE_REFUNDS_DASHBOARD,
      },
      {
        current: false,
        id: "archiveOrdersList",
        name: <Trans>Orders</Trans>,
        href: ROUTS.ARCHIVE_ORDERS_DASHBOARD,
      },
    ],
  },
].filter((item) => {
  if (!edmIsEnabled) {
    return item.id !== "warehouse";
  }

  return true;
});

export const navigationMenuSlim: typeSidebarData = [
  {
    id: "sumUp",
    name: <Trans>Sum up</Trans>,
    icon: ChartBarIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "sales",
        name: <Trans>Sales</Trans>,
        href: ROUTS.SUMUP_SALES_DASHBOARD,
        current: false,
      },
      {
        id: "refunds",
        name: <Trans>Refunds</Trans>,
        href: ROUTS.SUMUP_REFUNDS_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "warehouse",
    name: <Trans>Warehouse</Trans>,
    icon: HomeIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "warehouseProducts",
        name: <Trans>Warehouse products</Trans>,
        href: ROUTS.WAREHOUSE_PRODUCTS_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "commodity",
    name: <Trans>Commodity</Trans>,
    icon: ViewGridIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "products",
        name: <Trans>Products</Trans>,
        href: ROUTS.COMMODITY_PRODUCTS_DASHBOARD,
        current: false,
      },
      {
        id: "catalog",
        name: <Trans>Categories</Trans>,
        href: ROUTS.COMMODITY_CATEGORIES_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "till",
    name: <Trans>Tills</Trans>,
    icon: CalculatorIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "tills",
        name: <Trans>Tills list</Trans>,
        href: ROUTS.STORE_CASHIERDESKMANAGEMENT_DASHBOARD,
        current: false,
      },
      {
        id: "closingZReport",
        name: <Trans>Closing Z report</Trans>,
        href: ROUTS.SUMUP_CLOSINGZREPORT_DASHBOARD,
        current: false,
      },
      {
        id: "handoverRecord",
        name: <Trans>Handover record</Trans>,
        href: ROUTS.SUMUP_HANDOVERRECORD_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "orders",
    name: <Trans>Orders</Trans>,
    icon: TruckIcon,
    href: "#",
    current: false,
    children: [
      {
        id: "ordersList",
        name: <Trans>Orders list</Trans>,
        href: ROUTS.ORDERS_DASHBOARD,
        current: false,
      },
    ],
  },
  {
    id: "settings",
    name: <Trans>Settings</Trans>,
    icon: CogIcon,
    href: ROUTS.SETTINGS_DASHBOARD,
    current: false,
  },
  {
    href: "#",
    id: "archive",
    current: false,
    icon: ArchiveIcon,
    name: <Trans>Archive</Trans>,
    children: [
      {
        current: false,
        id: "archiveSalesList",
        name: <Trans>Sales</Trans>,
        href: ROUTS.ARCHIVE_SALES_DASHBOARD,
      },
      {
        current: false,
        id: "archiveRefundsList",
        name: <Trans>Refunds</Trans>,
        href: ROUTS.ARCHIVE_REFUNDS_DASHBOARD,
      },
      {
        current: false,
        id: "archiveOrdersList",
        name: <Trans>Orders</Trans>,
        href: ROUTS.ARCHIVE_ORDERS_DASHBOARD,
      },
    ],
  },
].filter((item) => {
  if (!edmIsEnabled) {
    return item.id !== "warehouse";
  }

  return true;
});
